import {
  EHeadingStyleVariant,
  EIconButtonVariant,
  GeneralCaretLeftIcon,
  Heading,
  IconButton,
  Modal,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ELocationSearchAutocompleteListVariant,
  LocationSearchAutocompleteList,
} from '@/components/common/LocationSearchAutocompleteList/LocationSearchAutocompleteList';
import { EPage, PAGE_PATHNAME } from '@/constants/pages';
import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import { useRouter } from '@/hooks/useRouter';
import { LocationGroupType, setSurfacedLocation } from '@/utility/surfacedLocation';

type TMobileSearchAddressModalContentProps = {
  onCloseHandler: () => void;
  location?: { lat: number; lng: number };
  address?: string;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  popularDestinations?: ILocationSearchAutocompleteListOption[];
  recentSearches?: ILocationSearchAutocompleteListOption[];
  handleRecentSearchNavigation?: (url: string) => void;
  isDeliveryMode?: boolean;
  useModeSwitcher?: boolean;
};

export const MobileSearchAddressModalContent = ({
  location,
  onCloseHandler,
  address,
  onChangeAddress,
  popularDestinations = [],
  recentSearches = [],
  handleRecentSearchNavigation,
  isDeliveryMode,
  useModeSwitcher,
}: TMobileSearchAddressModalContentProps) => {
  const intl = useIntl();
  const router = useRouter();
  const [addressValue, setAddressValue] = useState(address || '');
  const isEscapesSearchPage = router?.pathname?.includes(PAGE_PATHNAME[EPage.ESCAPES_SEARCH]);

  const placesOption = {
    text: addressValue,
    ...(location ? { location: [location.lng, location.lat] } : {}),
  };
  const suggestions = useLocationSearchSuggestions(placesOption.text, placesOption.location);

  const { groupedOptions, optionGroupLabels } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: true,
    suggestions,
    recentSearches,
    popularDestinations,
    includeRecentSearches: !isEscapesSearchPage,
  });

  const onChangeAddressHandler = (value: string) => {
    setAddressValue(value);
    setSurfacedLocation(LocationGroupType.OTHER);
  };

  const onSelectPopularDestination = (option: ILocationSearchAutocompleteListOption) => {
    if (!option) return;
    const { label, value } = option;
    if (value?.name) {
      setAddressValue(value.name);
      onChangeAddress?.(value.name, option);
    } else if (value?.address) {
      setAddressValue(value.address);
      onChangeAddress?.(value.address, option);
    } else {
      setAddressValue(label);
      onChangeAddress?.(label, option);
    }

    setSurfacedLocation(LocationGroupType.POPULAR_DESTINATIONS);
    onCloseHandler?.();
  };

  const onSelectLocation = (option: ILocationSearchAutocompleteListOption) => {
    if (!option) return;
    const { label, url, groupName, value } = option;

    if (groupName === optionGroupLabels.popularDestinations) {
      onSelectPopularDestination(option);
    }
    if (url) {
      setAddressValue(label.split('•').map(part => part.trim())[0] || '');
      handleRecentSearchNavigation?.(url);
      return;
    }

    setSurfacedLocation(LocationGroupType.MATCHES);

    if (value?.name) {
      setAddressValue(value.name);
      onChangeAddress?.(value.name, option);
    } else if (value?.address) {
      setAddressValue(value.address);
      onChangeAddress?.(value.address, option);
    } else {
      setAddressValue(label);
      onChangeAddress?.(label, option);
    }

    onCloseHandler?.();
  };

  const getModalTitle = () => {
    if (!useModeSwitcher) {
      return <FormattedMessage defaultMessage="Where are you heading?" id="g5vORI" />;
    }

    return isDeliveryMode ? (
      <FormattedMessage defaultMessage="Add a delivery location" id="nK3TK0" />
    ) : (
      <FormattedMessage defaultMessage="Add a pickup location" id="nrVolc" />
    );
  };

  const getInputLabel = () => {
    if (!useModeSwitcher) {
      return intl.formatMessage({ defaultMessage: 'Location', id: 'rvirM2' });
    }

    return isDeliveryMode
      ? intl.formatMessage({
          defaultMessage: 'Search a delivery location',
          id: 'tFvtJv',
          description: 'Location label for delivery mode',
        })
      : intl.formatMessage({
          defaultMessage: 'Pickup location',
          id: 'yHQgLI',
          description: 'Location label for RV rental mode',
        });
  };

  return (
    <>
      <Modal.Header inlineHeader={true} className="flex items-center gap-2 !mt-6">
        <IconButton
          icon={GeneralCaretLeftIcon}
          onClick={onCloseHandler}
          className="text-2xl"
          variant={EIconButtonVariant.Ghost}
        />
        <Heading variant={EHeadingStyleVariant.H6}>{getModalTitle()}</Heading>
      </Modal.Header>
      <Modal.Content className="!mt-5">
        <div className="flex flex-col gap-4 h-[70vh]">
          <TextInput
            controlled
            label={getInputLabel()}
            inputValue={addressValue}
            onChange={onChangeAddressHandler}
            showClearButton
          />
          <LocationSearchAutocompleteList
            groupedOptions={groupedOptions}
            onSelectOption={onSelectLocation}
            variant={ELocationSearchAutocompleteListVariant.Vertical}
          />
        </div>
      </Modal.Content>
    </>
  );
};
